// Status-Ampel je Dashboard-Perspektive (Konzept-Punkt "Status-Ampel"):
// ein Punkt pro Preset im Palette-Umschalter -- gruen = ruhig, amber =
// Warnungen aktiv, rot = Problem/Kritisch. "Alles" zeigt die schlechteste
// Farbe der Bereiche, damit der Umschalter schon VOR dem Wechsel sagt,
// wo es brennt.
//
// Bewusst eine ABLEITUNG aus den bestehenden Aggregationen (alerts-store
// fuer warn/problem, critical-summary fuer die Kritisch-Lane) -- keine
// eigene Pipeline, keine zweite Schwellen-Logik. Die Farb-Semantik ist
// das systemweite Severity-Vokabular:
//   critical | problem -> rot,  warning -> amber,  info -> ignoriert.
//
// Zuordnung Alert -> Welt ohne zweite Wahrheitsquelle: primaer der href
// des Alerts (#/zuhause/* = Zuhause, alles andere = Infrastruktur). Fuer
// Event-Alerts, deren Domain zwar Zuhause ist, die aber mangels eigener
// Zielseite ins Audit-Log deeplinken (door, motion, ...), korrigiert
// die HOME_DOMAINS-Liste nach.

import { getAlertsVisible } from "./alerts-store.svelte";
import type { Alert } from "./meldungs-logik";
import { getCriticalSummary } from "./critical-summary.svelte";

export type PerspectiveTone = "ok" | "warning" | "problem";

// Event-Domains, die zur Zuhause-Welt gehoeren, auch wenn ihr Alert-href
// nicht unter /zuhause liegt (kein eigenes Ziel -> /system/events).
const HOME_DOMAINS = new Set([
  "climate",
  "co2",
  "lipo",
  "waterleak",
  "door",
  "motion",
  "zigbee",
  "zwave",
  "esphome",
]);

type World = "infra" | "zuhause";

function alertWorld(a: Alert): World {
  if (a.domain && HOME_DOMAINS.has(a.domain)) return "zuhause";
  if (a.href?.startsWith("/zuhause")) return "zuhause";
  return "infra";
}

function worse(a: PerspectiveTone, b: PerspectiveTone): PerspectiveTone {
  if (a === "problem" || b === "problem") return "problem";
  if (a === "warning" || b === "warning") return "warning";
  return "ok";
}

function worldTone(world: World): PerspectiveTone {
  let tone: PerspectiveTone = "ok";
  for (const a of getAlertsVisible()) {
    if (alertWorld(a) !== world) continue;
    if (a.severity === "critical" || a.severity === "problem") tone = worse(tone, "problem");
    else if (a.severity === "warning") tone = worse(tone, "warning");
    // info: bewusst ignoriert -- die Ampel meldet Handlungsbedarf.
  }
  // Kritisch-Lane: eigene Pipeline (nicht quittierbar), zaehlt immer rot.
  const crit = getCriticalSummary();
  for (const ev of crit?.events ?? []) {
    const w: World = HOME_DOMAINS.has(ev.domain) ? "zuhause" : "infra";
    if (w === world) tone = "problem";
  }
  return tone;
}

// getPerspectiveTone: Ampelfarbe fuer eine Preset-Id aus dashboards.svelte
// ("alles" | "infra" | "zuhause"). In reaktivem Kontext aufrufen.
export function getPerspectiveTone(presetId: string): PerspectiveTone {
  if (presetId === "infra") return worldTone("infra");
  if (presetId === "zuhause") return worldTone("zuhause");
  // "alles" (und unbekannte Ids): worst-of ueber beide Welten.
  return worse(worldTone("infra"), worldTone("zuhause"));
}
